import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Crown, TrendingUp, MessageSquare, Loader2 } from 'lucide-react';
import { NewsletterForm } from '../components/NewsletterForm';
import { siteConfig, aboutData, topicsData } from '../data/siteData';
import { useVideos, useBlogPosts } from '../hooks/useContent';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function Home() {
  const featuredTopics = topicsData.slice(0, 3);
  
  // Fetch videos and blogs from Contentful
  const { videos = [], isLoading: videosLoading } = useVideos();
  const { blogPosts = [], isLoading: postsLoading } = useBlogPosts();
  
  // Get featured content from Contentful data (with safety checks)
  const featuredVideos = Array.isArray(videos) ? videos.slice(0, 2) : [];
  const featuredPosts = Array.isArray(blogPosts) ? blogPosts.slice(0, 3) : [];

  const iconMap = {
    Crown: Crown,
    TrendingUp: TrendingUp,
    MessageSquare: MessageSquare,
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={stagger}
            >
              <motion.p
                variants={fadeInUp}
                className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4"
              >
                {aboutData.subheadline}
              </motion.p>
              <motion.h1
                variants={fadeInUp}
                className="font-heading text-5xl md:text-7xl font-bold text-brand-primary leading-[1.1] mb-6"
              >
                {aboutData.headline}
              </motion.h1>
              <motion.p
                variants={fadeInUp}
                className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-xl mb-8"
              >
                {siteConfig.tagline} Helping organizations and individuals unlock their full potential through transformative leadership.
              </motion.p>
              
              {/* Inline Newsletter Form */}
              <motion.div variants={fadeInUp}>
                <p className="text-sm font-medium text-brand-primary mb-3">Join my newsletter for weekly insights:</p>
                <NewsletterForm variant="inline" showAnimation={false} />
              </motion.div>
            </motion.div>

            {/* Right - Profile Picture */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <img
                src={siteConfig.profileImage}
                alt={siteConfig.name}
                className="w-72 md:w-80 lg:w-96 h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Areas of Expertise
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-brand-primary">
              Topics I Cover
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {featuredTopics.map((topic, index) => {
              const IconComponent = iconMap[topic.icon] || Crown;
              return (
                <motion.div
                  key={topic.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/topics?topic=${topic.id}`}
                    className="block p-8 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                  >
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${topic.color}15` }}
                    >
                      <IconComponent className="w-7 h-7" style={{ color: topic.color }} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-primary mb-3 group-hover:text-brand-secondary transition-colors">
                      {topic.name}
                    </h3>
                    <p className="text-slate-600">{topic.description}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link
              to="/topics"
              className="inline-flex items-center text-brand-secondary font-semibold hover:gap-2 transition-all"
            >
              View All Topics <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Videos Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
                Latest Episodes
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-brand-primary">
                Watch & Listen
              </h2>
            </div>
            <Link
              to="/videos"
              className="hidden md:inline-flex items-center text-brand-secondary font-semibold hover:gap-2 transition-all mt-4 md:mt-0"
            >
              View All Videos <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </motion.div>

          {videosLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
            </div>
          ) : featuredVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {featuredVideos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/videos/${video.slug}`} className="group block">
                    <div className="relative aspect-video rounded-2xl overflow-hidden mb-4">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-brand-secondary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <Play className="w-6 h-6 text-white ml-1" fill="white" />
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm">
                          {video.duration}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-primary group-hover:text-brand-secondary transition-colors mb-2">
                      {video.title}
                    </h3>
                    <p className="text-slate-600 line-clamp-2">{video.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-brand-accent rounded-2xl">
              <p className="text-slate-600">No videos available yet. Add content in Contentful.</p>
            </div>
          )}

          <div className="text-center mt-8 md:hidden">
            <Link
              to="/videos"
              className="inline-flex items-center text-brand-secondary font-semibold"
            >
              View All Videos <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Blog Posts */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
                From the Blog
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-brand-primary">
                Latest Insights
              </h2>
            </div>
            <Link
              to="/blog"
              className="hidden md:inline-flex items-center text-brand-secondary font-semibold hover:gap-2 transition-all mt-4 md:mt-0"
            >
              View All Posts <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </motion.div>

          {postsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
            </div>
          ) : featuredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-primary group-hover:text-brand-secondary transition-colors mb-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 line-clamp-2">{post.excerpt}</p>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl">
              <p className="text-slate-600">No blog posts available yet. Add content in Contentful.</p>
            </div>
          )}

          <div className="text-center mt-8 md:hidden">
            <Link
              to="/blog"
              className="inline-flex items-center text-brand-secondary font-semibold"
            >
              View All Posts <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
